<template>
  <div class="max-w-7xl mx-auto p-6">
    <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-1">Camiones</h1>
        <p class="text-gray-600">
          Modelos, fichas y fotos del sitio
          <a
            :href="siteUrl"
            target="_blank"
            rel="noopener"
            class="text-blue-600 hover:underline"
            >{{ siteUrl.replace("https://", "") }}</a
          >.
        </p>
      </div>
      <div class="flex space-x-3">
        <button
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          @click="showSeries = true"
        >
          <i class="mdi mdi-format-list-group mr-2"></i>
          Series ({{ series.length }})
        </button>
        <button
          class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          @click="editTruck(null)"
        >
          <i class="mdi mdi-plus mr-2"></i>
          Nuevo camión
        </button>
      </div>
    </div>

    <div
      class="bg-white rounded-lg border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4"
    >
      <input
        v-model="search"
        type="text"
        placeholder="Buscar modelo…"
        class="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64"
      />
      <select
        v-model="statusFilter"
        class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
      >
        <option value="all">Todos</option>
        <option value="active">Publicados</option>
        <option value="inactive">Ocultos</option>
      </select>
      <span class="text-sm text-gray-500 ml-auto">
        {{ filteredTrucks.length }} de {{ trucks.length }} modelos
      </span>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
      <BaseSmallSpinner />
    </div>

    <template v-else>
      <section v-for="group in groups" :key="group.key" class="mb-8">
        <div class="flex items-center mb-3">
          <h2 class="text-lg font-semibold text-gray-800">{{ group.name }}</h2>
          <span
            v-if="group.series && group.series.status == '0'"
            class="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700"
            >Serie oculta</span
          >
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            v-for="truck in group.trucks"
            :key="truck.id"
            class="text-left bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            :class="{ 'opacity-60': truck.status == '0' }"
            @click="editTruck(truck)"
          >
            <div class="h-36 bg-gray-100 flex items-center justify-center">
              <img
                v-if="truck.imageUrl"
                :src="assetUrl(truck.imageUrl)"
                :alt="truck.name"
                class="w-full h-full object-cover"
              />
              <i v-else class="mdi mdi-truck text-5xl text-gray-300"></i>
            </div>
            <div class="p-3">
              <p class="font-semibold text-gray-900">{{ truck.name }}</p>
              <p class="text-xs text-gray-500 mb-2">
                /camiones/{{ truck.slug }}
              </p>
              <div class="flex flex-wrap gap-1">
                <span
                  v-if="truck.status == '0'"
                  class="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700"
                  >Oculto</span
                >
                <span
                  v-if="truck.featured == '1'"
                  class="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800"
                  >Destacado</span
                >
                <span
                  v-if="!truck.datasheetUrl"
                  class="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700"
                  >Sin ficha PDF</span
                >
              </div>
            </div>
          </button>
        </div>
      </section>
      <p v-if="!groups.length" class="text-center text-gray-500 py-12">
        No hay camiones con esos filtros.
      </p>
    </template>

    <TruckEditorModal
      v-if="editing"
      :truckId="editing.id"
      :series="series"
      @close="editing = null"
      @saved="onSaved"
      @deleted="onDeleted"
    />
    <TruckSeriesModal
      v-if="showSeries"
      :series="series"
      @close="showSeries = false"
      @changed="loadTrucks"
    />
  </div>
</template>

<script>
import {
  getTrucks,
  truckAssetUrl,
  TRUCKS_SITE_URL
} from "@/api/store/TrucksService.js";
import TruckEditorModal from "@/components/trucks/TruckEditorModal.vue";
import TruckSeriesModal from "@/components/trucks/TruckSeriesModal.vue";

export default {
  name: "TrucksPage",
  components: { TruckEditorModal, TruckSeriesModal },
  data() {
    return {
      siteUrl: TRUCKS_SITE_URL,
      series: [],
      trucks: [],
      loading: false,
      search: "",
      statusFilter: "all",
      editing: null,
      showSeries: false
    };
  },
  computed: {
    filteredTrucks() {
      const term = this.search.trim().toLowerCase();
      return this.trucks.filter(truck => {
        if (this.statusFilter === "active" && truck.status != "1") return false;
        if (this.statusFilter === "inactive" && truck.status != "0")
          return false;
        return !term || truck.name.toLowerCase().includes(term);
      });
    },
    groups() {
      const groups = this.series.map(serie => ({
        key: serie.id,
        name: serie.name,
        series: serie,
        trucks: this.filteredTrucks.filter(t => t.seriesId == serie.id)
      }));
      groups.push({
        key: "none",
        name: "Sin serie",
        series: null,
        trucks: this.filteredTrucks.filter(t => !t.seriesId)
      });
      return groups.filter(group => group.trucks.length);
    }
  },
  mounted() {
    this.loadTrucks();
  },
  methods: {
    assetUrl: truckAssetUrl,
    async loadTrucks() {
      this.loading = true;
      try {
        const res = await getTrucks();
        this.series = res.series || [];
        this.trucks = res.trucks || [];
      } catch (e) {
        this.$toast.open({
          message: "No se pudieron cargar los camiones",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    editTruck(truck) {
      this.editing = { id: truck ? truck.id : null };
    },
    onSaved() {
      this.loadTrucks();
    },
    onDeleted() {
      this.editing = null;
      this.loadTrucks();
    }
  }
};
</script>
